import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import Button from 'components/Button';
import { usePermissionService } from 'services/permission';
import { Permission } from 'types/permission';
import styles from './Permission.module.scss';

interface EditPermissionModalProps {
  show: boolean;
  onHide: () => void;
  permission: Permission | null;
  onSuccess: (permission: Permission) => void;
  onError: (message: string) => void;
}

const EditPermissionModal: React.FC<EditPermissionModalProps> = ({
  show,
  onHide,
  permission,
  onSuccess,
  onError,
}) => {
  const permissionService = usePermissionService();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    key: '',
    name: '',
    description: '',
  });

  useEffect(() => {
    if (permission) {
      setFormData({
        key: permission.key,
        name: permission.name,
        description: permission.description || '',
      });
    }
  }, [permission]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permission) return;

    setLoading(true);
    try {
      const updatedPermission = await permissionService.updatePermission(
        permission.id,
        formData,
      );
      onSuccess(updatedPermission);
    } catch (error) {
      console.error('Failed to update permission:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to update permission. Please try again.';
      onError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Permission</Modal.Title>
      </Modal.Header>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Modal.Body>
          <div className={styles.formGroup}>
            <label className={styles.label}>Key</label>
            <input
              type="text"
              name="key"
              className={styles.input}
              value={formData.key}
              onChange={handleChange}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Name</label>
            <input
              type="text"
              name="name"
              className={styles.input}
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Description</label>
            <textarea
              name="description"
              className={styles.textarea}
              value={formData.description}
              onChange={handleChange}
              rows={3}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={onHide}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Updating...' : 'Update Permission'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditPermissionModal;
